import Vue from 'vue';
import $ from 'jquery';
import { mapGetters } from 'vuex';
import Popper from 'vue-popperjs';
import Modal from 'src/components/widgets/Modal.vue';
import Query from 'src/api/query';
import QueryUtils, { hasUnstructured, expandQuery } from 'src/utils/query';
import store from 'src/store';
import { CLEAR_REQUEST_ERRORS, LOAD_SAVED_QUERIES } from 'src/store/types';
import { BfTextInput, BfButton } from 'components/Butterfly';
import { Form, Field } from 'components/forms';
const SavedQueries = Vue.extend({
    components: {
        Popper,
        Modal,
        Form,
        Field,
        BfTextInput,
        BfButton,
    },
    props: {
        compare: { type: Boolean, default: false }
    },
    data() {
        return {
            defaultQueryId: null,
            isLoading: false,
            saveQueryRows: [],
            saveQueryLevel: "frame",
            savedQueryAddDashboard: false,
            selectedDeleteQuery: null,
            selectedRenameQuery: null,
            addToDashboards: [],
            initialFormValues: {
                query_name: '',
                description: '',
            }
        };
    },
    computed: {
        ...mapGetters([
            'currentProject', 'currentAnalysis', 'currentSite',
            'savedQueries', 'featureFlags',
        ]),
        savedQueryValue() {
            let queryJson = QueryUtils.queryRowsToBotanic(this.saveQueryRows, this.saveQueryLevel);
            queryJson.level = this.saveQueryLevel || "frame";
            return queryJson;
        },
        canAddToDashboard() {
            return hasUnstructured(expandQuery('newquery', this.savedQueryValue, this.savedQueries));
        },
        dashboardList() {
            return this.currentAnalysis?.dashboards || [];
        },
        deleteConflicts() {
            if (!this.selectedDeleteQuery)
                return [];
            const conflicts = [];
            const id = this.selectedDeleteQuery.id.toString();
            this.savedQueries.forEach((query) => {
                const rows = QueryUtils.botanicToQueryRows(query.query_value);
                rows.forEach((row) => {
                    if (row.type === 'query' && row.values.includes(id)) {
                        conflicts.push(query);
                    }
                });
            });
            return conflicts;
        }
    },
    watch: {
        dashboardList: {
            immediate: true,
            deep: true,
            handler(v) {
                if (this.addToDashboards.length === 0) {
                    this.addToDashboards = v.map(dashboard => dashboard.id);
                }
            },
        },
    },
    mounted() {
        this.refresh();
        this.$nextTick(() => {
            $('.ui.checkbox').checkbox();
        });
    },
    methods: {
        resetForm(values) {
            this.$refs.saveQueryModalForm.resetForm(values);
        },
        selectAllDashboards() {
            this.addToDashboards = this.dashboardList.map(d => d.id);
        },
        deSelectAllDashboards() {
            this.addToDashboards = [];
        },
        // Clear selected query
        clearSelection() {
            $(this.$el.querySelector('div.dropdown')).dropdown('clear');
        },
        // Delete the currently selected query
        async deleteQuery() {
            this.isLoading = true;
            const id = this.selectedDeleteQuery.id.toString();
            // Save a list of updated queries in case the current state needs updating
            const updatedQueries = [];
            // Update queries that contain the query to be deleted
            this.deleteConflicts.forEach(async (query) => {
                let level = query.query_value.level ? query.query_value.level : 'frame';
                const rows = QueryUtils.botanicToQueryRows(query.query_value);
                // Remove the query where it appears in any query rows
                rows.forEach((row, i) => {
                    if (row.type === 'query') {
                        // If multiple queries in this row, just filter out the
                        // one we're deleting.
                        row.values = row.values.filter((v) => v !== id);
                        // If we're left with no queries in this row, remove the row
                        if (!row.values.length) {
                            rows.splice(i, 1);
                        }
                    }
                });
                // If we're left with no rows in the "parent" query, delete the query.
                if (rows.length === 0) {
                    await Query.deleteSavedQuery(query.project, query.analysis, query.id);
                }
                else {
                    // Otherwise, update the "parent" query with the rows we modified
                    // above to remove the reference to the query we're deleting.
                    let data = await Query.updateSavedQuery(query.project, query.analysis, {
                        query_value: QueryUtils.queryRowsToBotanic(rows, level),
                        id: query.id,
                    });
                    updatedQueries.push(data);
                }
            });
            Query.deleteSavedQuery(this.currentProject.id, this.currentAnalysis.id, this.selectedDeleteQuery.id)
                .then(() => {
                let queryId = this.selectedDeleteQuery.id;
                this.$emit('query-deleted', queryId, updatedQueries);
                if (`${queryId}` === $(this.$el.querySelector('div.dropdown')).dropdown('get value')) {
                    this.clearSelection();
                }
                this.hideDeleteQueryModal();
                this.refresh();
                this.$analytics.track.query.delete(queryId);
            })
                .finally(() => {
                this.isLoading = false;
            });
        },
        // Hide the delete query modal
        hideDeleteQueryModal() {
            this.$refs.deleteQueryModal.hide();
            this.selectedDeleteQuery = null;
        },
        // Hide the save query modal
        hideSaveQueryModal() {
            this.$refs.saveQueryModal.hide();
            this.resetForm({
                query_name: '',
                description: '',
            });
        },
        _clearSaveQueryModalState() {
            this.saveQueryRows = [];
            this.resetForm({
                query_name: '',
                description: '',
            });
            this.selectedRenameQuery = null;
            this.savedQueryAddDashboard = false;
        },
        // Emit query selected event
        onQuerySelected(query) {
            this.$emit('query-selected', query);
        },
        // Prompt delete for the specified `query`
        promptDeleteQuery(query) {
            this.selectedDeleteQuery = query;
            this.$refs.deleteQueryModal.show();
        },
        // Prompt rename for the specified `query`
        promptRenameQuery(query) {
            this.selectedRenameQuery = query;
            this.resetForm({
                query_name: query.name,
                description: query.description,
            });
            this.$refs.saveQueryModal.show();
        },
        // Prompt save for the specified `query`
        promptSaveQuery(queryRows, queryScope) {
            this.saveQueryRows = queryRows;
            this.saveQueryLevel = queryScope;
            this.resetForm({
                query_name: '',
                description: '',
            });
            if (this.canAddToDashboard) {
                this.savedQueryAddDashboard = true; // default checkbox to checked
            }
            this.$refs.saveQueryModal.show();
        },
        // Update list of saved queries
        refresh(idToSelect) {
            let firstLoad = !this.savedQueries;
            store.dispatch({
                type: LOAD_SAVED_QUERIES,
                projectId: this.$route.params.projectId,
                analysisId: this.$route.params.analysisId
            })
                .then(() => {
                this.$nextTick(() => {
                    let dropdown = $(this.$el.querySelector('div.dropdown')).dropdown();
                    if (idToSelect) {
                        dropdown.dropdown('set selected', idToSelect);
                    }
                    else if (this.defaultQueryId) {
                        dropdown.dropdown('set selected', this.defaultQueryId);
                    }
                    if (firstLoad) {
                        this.$emit('loaded');
                    }
                });
            });
        },
        // Rename a query
        async renameQuery(values) {
            this.isLoading = true;
            let queryId = this.selectedRenameQuery.id;
            await Query.updateSavedQuery(this.currentProject.id, this.currentAnalysis.id, {
                id: this.selectedRenameQuery.id,
                name: values.query_name,
                description: values.description || '' // Can be blank but not null
            })
                .then((data) => {
                this.$emit('query-renamed', data);
                this.hideSaveQueryModal();
                // Reset state
                this.refresh();
                this.$analytics.track.query.rename(queryId, values.query_name);
            })
                .catch((error) => {
                this.$store.dispatch(CLEAR_REQUEST_ERRORS); // custom error handling
                let nonFieldErrors = error['non_field_errors'] || [''];
                if (nonFieldErrors[0].toLowerCase().includes('name')) {
                    this.$refs.saveQueryModalForm.setErrors({
                        'query_name': 'A theme with this name already exists.'
                    });
                }
            })
                .finally(() => {
                this.isLoading = false;
            });
        },
        // Save the currently set query value
        saveQuery(values) {
            this.isLoading = true;
            const saveFn = Query.createSavedQueryV2(this.currentProject.id, this.currentAnalysis.id, {
                query_value: this.savedQueryValue,
                name: values.query_name,
                description: values.description,
                dashboard_ids: this.addToDashboards,
            });
            saveFn.then((data) => {
                let query = data;
                this.$emit('query-saved', query);
                this.hideSaveQueryModal();
                // Reset state
                this.refresh(query.id);
                this.$analytics.track.query.save(query.id, values.query_name, JSON.stringify(this.savedQueryValue));
            })
                .catch((error) => {
                let nonFieldErrors = error['non_field_errors'] || [''];
                if (nonFieldErrors[0].toLowerCase().includes('name')) {
                    this.$refs.saveQueryModalForm.setErrors({
                        'query_name': 'A theme with this name already exists.'
                    });
                    this.$store.dispatch(CLEAR_REQUEST_ERRORS); // custom error handling
                }
            })
                .finally(() => {
                this.isLoading = false;
            });
        },
        // Select a saved query in the dropdown by ID
        selectQuery(id) {
            this.$nextTick(() => {
                if (this.savedQueries.find((q) => q.id === parseInt(id, 10))) {
                    $(this.$el.querySelector('div.dropdown')).dropdown('set selected', id);
                }
                else {
                    this.clearSelection();
                }
            });
        },
    }
});
export default SavedQueries;
